import { zodResolver } from '@hookform/resolvers/zod'
import type { HttpError } from '@refinedev/core'
import { useForm } from '@refinedev/react-hook-form'
import type { Tenant } from '@sirius/contracts/admin'
import { tenantCreateInputSchema } from '@sirius/contracts/admin'
import { useTranslation } from 'react-i18next'
import type { z } from 'zod'
import { Button } from '~/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import {
  FORM_RECORD_QUERY_OPTIONS,
  useRecordPending,
} from '~/lib/formRecordQuery'
import { NotFoundPage } from '~/pages/notFound'
import { suggestSlug } from './suggestSlug'

/** The create input with a required status (the select always has one). */
const tenantFormSchema = tenantCreateInputSchema.extend({
  status: tenantCreateInputSchema.shape.status.unwrap(),
})
type TenantFormValues = z.input<typeof tenantFormSchema>

export function TenantForm({
  action,
  id,
}: {
  action: 'create' | 'edit'
  id?: string
}) {
  const { t } = useTranslation()
  const form = useForm<Tenant, HttpError, TenantFormValues>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: { name: '', slug: '', status: 'active' },
    refineCoreProps: {
      resource: 'tenants',
      action,
      id,
      redirect: 'show',
      queryOptions: FORM_RECORD_QUERY_OPTIONS,
    },
  })
  const { onFinish, formLoading, query } = form.refineCore
  // in edit mode nothing may be typed or saved before the record has settled
  const pending = useRecordPending(action, query)

  if (query?.error?.statusCode === 404) {
    return <NotFoundPage />
  }
  if (query?.error) {
    return (
      <p
        role="alert"
        className="text-destructive text-sm"
      >
        {t('tenantsPage.common.loadError', { message: query.error.message })}
      </p>
    )
  }

  return (
    <Form {...form}>
      <form
        aria-busy={pending}
        onSubmit={form.handleSubmit(onFinish)}
        className="max-w-md space-y-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('tenantsPage.common.name')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={pending}
                  onChange={(event) => {
                    field.onChange(event)
                    // suggest a slug until the user edits it; not dirtying
                    // it keeps the suggestion going
                    if (
                      action === 'create' &&
                      !form.getFieldState('slug').isDirty
                    ) {
                      form.setValue('slug', suggestSlug(event.target.value), {
                        shouldDirty: false,
                      })
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="slug"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('tenantsPage.common.slug')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={pending}
                />
              </FormControl>
              <FormDescription>
                {action === 'edit'
                  ? t('tenantsPage.form.slugHintEdit')
                  : t('tenantsPage.form.slugHintCreate')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('tenantsPage.common.status')}</FormLabel>
              <Select
                value={field.value}
                disabled={pending}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger aria-label={t('tenantsPage.common.status')}>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="active">
                    {t('tenantsPage.common.statusValue.active')}
                  </SelectItem>
                  <SelectItem value="suspended">
                    {t('tenantsPage.common.statusValue.suspended')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={formLoading || pending}
        >
          {t('tenantsPage.form.save')}
        </Button>
      </form>
    </Form>
  )
}
